import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2, Zap, Loader2 } from "lucide-react";
import { VLYT } from "@/constants/testIds/vlyt";
import { joinWaitlist, logInstallEvent } from "@/lib/api";

export default function WaitlistModal({ open, onOpenChange, source = "generic" }) {
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState("rider");
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSuccess(false);
      setEmail("");
      setRole("rider");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await joinWaitlist({ email, role, source });
      await logInstallEvent(source && source.includes("install_banner") ? "banner_install" : "banner_view");
      setSuccess(true);
      toast.success("You're on the list.", {
        description: `We'll email ${email} when access opens.`,
      });
    } catch {
      toast.error("Could not join the waitlist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={VLYT.waitlist.modal}
        className="bg-[color:var(--vlyt-surface-1)] border border-[color:var(--vlyt-border)] text-[color:var(--vlyt-text)] max-w-lg"
      >
        {success ? (
          <div className="py-4" data-testid={VLYT.waitlist.success}>
            <div className="h-14 w-14 rounded-full bg-[rgba(191,255,0,0.08)] flex items-center justify-center mb-5 ring-1 ring-[color:var(--vlyt-lime)]">
              <CheckCircle2 size={26} className="text-lime" />
            </div>
            <DialogTitle className="text-h3 font-display">You're on the list.</DialogTitle>
            <DialogDescription className="text-[color:var(--vlyt-muted)] mt-2">
              We'll email <span className="text-[color:var(--vlyt-text)]">{email}</span> the moment access opens on your campus. Meanwhile, tell a friend.
            </DialogDescription>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <span className="h-6 w-6 rounded-md bg-lime flex items-center justify-center">
                  <Zap size={14} className="text-[color:var(--vlyt-base)]" strokeWidth={3} />
                </span>
                <span className="text-eyebrow">Early Access · Cohort 01</span>
              </div>
              <DialogTitle className="text-h3 font-display">Join the Vlyt waitlist</DialogTitle>
              <DialogDescription className="text-[color:var(--vlyt-muted)]">
                We roll out campus-by-campus. Get in line: riders and drivers welcome.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="wl-email" className="text-[color:var(--vlyt-muted)] font-mono text-xs uppercase tracking-widest">Email</Label>
              <Input
                id="wl-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@stu.cu.edu.ng"
                data-testid={VLYT.waitlist.email}
                className="bg-[color:var(--vlyt-surface-2)] border-[color:var(--vlyt-border)] h-12 text-base"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-[color:var(--vlyt-muted)] font-mono text-xs uppercase tracking-widest">I want to</Label>
              <RadioGroup
                value={role}
                onValueChange={setRole}
                data-testid={VLYT.waitlist.role}
                className="grid grid-cols-2 gap-3"
              >
                {[
                  { v: "rider", title: "Ride", sub: "I need mobility on campus" },
                  { v: "driver", title: "Drive", sub: "I want to earn on Vlyt" },
                ].map((o) => (
                  <label
                    key={o.v}
                    htmlFor={`role-${o.v}`}
                    className={`cursor-pointer rounded-2xl border p-4 transition ${
                      role === o.v
                        ? "border-lime bg-[rgba(191,255,0,0.06)]"
                        : "border-[color:var(--vlyt-border)] hover:border-[color:var(--vlyt-purple-soft)]"
                    }`}
                  >
                    <RadioGroupItem id={`role-${o.v}`} value={o.v} className="sr-only" />
                    <div className="text-h3 text-[18px] font-display">{o.title}</div>
                    <div className="text-[13px] text-[color:var(--vlyt-muted)] mt-1">{o.sub}</div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <button
              type="submit"
              disabled={loading || !email}
              data-testid={VLYT.waitlist.submit}
              className="btn-lime w-full justify-center h-12 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} strokeWidth={2.8} />}
              {loading ? "Joining…" : "Reserve my spot"}
            </button>

            <p className="text-[12px] text-[color:var(--vlyt-muted)] text-center">
              By joining, you agree to our <a href="/privacy" className="underline hover:text-lime">Privacy Policy</a>.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
